import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useUserStore } from '@/store/userStore';
import { validateLoginStatus } from '@/api/user';

interface ValidateLoginOptions {
	onValidateComplete?: () => void;
}

// 不需要登录验证的路径列表
const PUBLIC_PATHS = [
	'/user/login',
	'/user/terms',
	'/user/privacy'
];

/**
 * 验证登录状态的 hook
 */
export const useValidateLogin = (options?: ValidateLoginOptions) => {
	const navigate = useNavigate();
	const { setUserInfo, clearUserInfo, userInfo } = useUserStore();
	const location = useLocation();
	const validatingRef = useRef(false);

	useEffect(() => {
		const validateToken = async () => {
			try {
				// 检查是否是公开路径
				if (PUBLIC_PATHS.includes(location.pathname)) {
					options?.onValidateComplete?.();
					return;
				}

				// 如果已经有用户信息且不是登录页，不需要重复验证
				if (userInfo && location.pathname !== '/user/login') {
					options?.onValidateComplete?.();
					return;
				}

				// 防止重复验证
				if (validatingRef.current) {
					return;
				}

				validatingRef.current = true;
				const token = Cookies.get('token');
				
				if (!token) {
					clearUserInfo();
					if (location.pathname !== '/user/login') {
						navigate('/user/login', { replace: true });
					}
					options?.onValidateComplete?.();
					return;
				}

				const { data } = await validateLoginStatus();
				
				if (data.code === 0 && data.data?.userInfo) {
					console.log('Setting new userInfo:', data.data.userInfo);
					setUserInfo(data.data.userInfo);
					options?.onValidateComplete?.();
				} else {
					clearUserInfo();
					if (location.pathname !== '/user/login') {
						navigate('/user/login', { replace: true });
					}
				}
			} catch (error) {
				console.error('Validation error:', error);
				clearUserInfo();
				if (location.pathname !== '/user/login') {
					navigate('/user/login', { replace: true });
				}
			} finally {
				validatingRef.current = false;
				options?.onValidateComplete?.();
			}
		};

		validateToken();
	}, [navigate, setUserInfo, clearUserInfo, location.pathname, userInfo]);

	return userInfo;
};

/**
 * 获取用户信息的 hook
 */
export const useUserInfo = () => {
	const userInfo = useUserStore(state => state.userInfo);
	return userInfo;
};

/**
 * 退出登录的 hook
 */
export const useLogout = () => {
	const navigate = useNavigate();
	const { clearUserInfo } = useUserStore();

	return () => {
		Cookies.remove('token');
		clearUserInfo();
		navigate('/user/login', { replace: true });
	};
};
